import type { TCreateSlice } from "@/store";

type TNotificationKind = "toast";
type TNotificationSeverity = "success" | "warning" | "danger" | "info";

type TNotification = {
    id: string;
    kind: TNotificationKind;
    title: string;
    severity: TNotificationSeverity;
    message?: string;
};

type TStateNotifications = {
    notifications: TNotification[];
};

type TActionsPublicNotifications = {
    addNotification: (notification: Omit<TNotification, "id">) => string;
    addNotificationWithDefaultTimeout: (
        notification: Omit<TNotification, "id">,
    ) => void;
    addNotificationWithTimeout: (
        notification: Omit<TNotification, "id">,
        timeout: number,
    ) => void;
    removeNotification: (id: string) => void;
};

type TDerivedStateNotifications = {
    selectToasts: (state: TSliceNotifications) => TNotification[];
};

export type TSliceNotifications = TStateNotifications &
    TActionsPublicNotifications &
    TDerivedStateNotifications;

export const initialStateNotifications = {
    notifications: [],
} as const satisfies TStateNotifications;

export const DEFAULT_NOTIFICATIONS_TIMEOUT = 5000;

// Simple UUID generation function
const generateId = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
            const r = (Math.random() * 16) | 0,
                v = c == "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        },
    );
};

export const createSliceNotifications: TCreateSlice<TSliceNotifications> =
    () => (set, get) => {
        const actionsPublic: TActionsPublicNotifications = {
            addNotification: notification => {
                const id = generateId();
                set(
                    state => ({
                        notifications: [
                            ...state.notifications,
                            { ...notification, id },
                        ],
                    }),
                    false,
                    "notifications/addNotification",
                );
                return id;
            },
            addNotificationWithDefaultTimeout: notification => {
                const id = generateId();
                set(
                    state => ({
                        notifications: [
                            ...state.notifications,
                            { ...notification, id },
                        ],
                    }),
                    false,
                    "notifications/addNotificationWithDefaultTimeout",
                );
                setTimeout(
                    () => get().removeNotification(id),
                    DEFAULT_NOTIFICATIONS_TIMEOUT,
                );
            },
            addNotificationWithTimeout: (notification, timeout) => {
                const id = generateId();
                set(
                    state => ({
                        notifications: [
                            ...state.notifications,
                            { ...notification, id },
                        ],
                    }),
                    false,
                    "notifications/addNotificationWithTimeout",
                );
                setTimeout(() => get().removeNotification(id), timeout);
            },
            removeNotification: id => {
                set(
                    state => ({
                        notifications: state.notifications.filter(
                            notification => notification.id !== id,
                        ),
                    }),
                    false,
                    "notifications/removeNotification",
                );
            },
        };

        const derivedState: TDerivedStateNotifications = {
            selectToasts: state => {
                return state.notifications.filter(
                    notification => notification.kind === "toast",
                );
            },
        };

        return {
            ...initialStateNotifications,
            ...actionsPublic,
            ...derivedState,
        };
    };
